// Trae las métricas de las tiendas de la marca desde atlas-backend —
// ADR-2026-10-06-metricas-de-tienda-en-el-backend.
import { useCallback, useEffect, useRef, useState } from 'react';
import { atlasBackendGet } from '../../../lib/atlasBackend';
import { useBrandProfile } from '../../../hooks/useBrandProfile';
import { getTnStatus } from '../../../lib/tiendaNube';
import { estadoDeMercadoLibre } from '../../../lib/mercadoLibreConexion';
import type { Periodo, RespuestaMetricas } from './tipos';

export async function fetchMetricasDeTienda(
  brandId: string | null,
  dias: Periodo,
  fresco = false,
): Promise<RespuestaMetricas> {
  const q = new URLSearchParams({ dias: String(dias) });
  if (brandId) q.set('brand_id', brandId);
  if (fresco) q.set('fresco', '1');
  const res = await atlasBackendGet(`/v1/metricas/tienda?${q.toString()}`);
  const cuerpo = (await res.json().catch(() => null)) as (RespuestaMetricas & { ok?: boolean }) | null;
  if (!res.ok || !cuerpo?.ok) throw new Error(`metricas: ${res.status}`);
  return cuerpo;
}

/**
 * Si el pedido de métricas falla, ¿hay algo que leer? Con ninguna tienda conectada un
 * error no le sirve a nadie: lo que corresponde es invitar a conectar. Pregunta lo mismo
 * que Mi marca (la edge function de Tienda Nube y el estado de Mercado Libre) y, si
 * ninguna está conectada, devuelve la respuesta de "sin tiendas". Si alguna lo está, o
 * no se pudo saber, devuelve `null` y queda el error.
 */
export async function sinTiendasConectadas(brandId: string | null, dias: Periodo): Promise<RespuestaMetricas | null> {
  const [tn, ml] = await Promise.all([getTnStatus(brandId), estadoDeMercadoLibre(brandId)]);
  // Si no se pudo preguntar por alguna de las dos, no se sabe si hay tiendas: queda el error con
  // "Reintentar" en vez de mostrar como desconectada una cuenta que quizás está conectada (review #168).
  if (tn.error || ml.error || tn.connected || (ml.disponible && ml.conectada)) return null;
  return {
    dias,
    generadoAt: new Date().toISOString(),
    tiendanube: { estado: 'sin_conexion' },
    mercadolibre: ml.disponible ? { estado: 'sin_conexion' } : { estado: 'no_disponible' },
  };
}

/** Lo guardado, si es de esta marca y de este período (o si el período nuevo todavía carga). */
export function datosVigentes(
  guardado: { marca: string | null; r: RespuestaMetricas } | null,
  marca: string | null,
  dias: Periodo,
  cargando: boolean,
): RespuestaMetricas | null {
  if (!guardado || guardado.marca !== marca) return null;
  return guardado.r.dias === dias || cargando ? guardado.r : null;
}

/**
 * Las métricas del período para la marca activa. Mantiene los datos anteriores mientras carga
 * el período nuevo (la pantalla no se vacía al cambiar de 30 a 7 días), y descarta respuestas
 * viejas si el período cambió en el medio.
 */
export function useMetricasDeTienda(dias: Periodo, activo = true) {
  const { currentBrandId, loading: cargandoMarca } = useBrandProfile();
  // Se espera a la marca: sin ella el pedido saldría por la marca por defecto y se repetiría
  // apenas llega la activa (dos lecturas de las APIs de las tiendas por cada apertura).
  const listo = activo && !cargandoMarca;
  // Con la marca de la que son: al cambiar de marca no se muestran los números de la anterior.
  const [guardado, setGuardado] = useState<{ marca: string | null; r: RespuestaMetricas } | null>(null);
  const [cargando, setCargando] = useState(activo);
  const [error, setError] = useState(false);
  const pedido = useRef(0);

  const cargar = useCallback(
    async (fresco = false) => {
      if (!listo) return;
      const n = ++pedido.current;
      setCargando(true);
      setError(false);
      try {
        const r = await fetchMetricasDeTienda(currentBrandId, dias, fresco);
        if (n === pedido.current) setGuardado({ marca: currentBrandId, r });
      } catch {
        const vacia = await sinTiendasConectadas(currentBrandId, dias).catch(() => null);
        if (n === pedido.current) {
          if (vacia) setGuardado({ marca: currentBrandId, r: vacia });
          else setError(true);
        }
      } finally {
        if (n === pedido.current) setCargando(false);
      }
    },
    [listo, currentBrandId, dias],
  );

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Los datos se muestran sólo si son de la marca y del período pedidos. Mientras carga el período
  // nuevo se dejan los anteriores (la pantalla no se vacía), y la pantalla los rotula con SU período
  // (`datos.dias`). Si el pedido nuevo falla, dejan de mostrarse y se ve el error: antes quedaban los
  // números de 30 días bajo "7 días" (review #168).
  const datos = datosVigentes(guardado, currentBrandId, dias, cargando);
  return { datos, cargando, error, recargar: () => void cargar(true) };
}
